import { useCallback, useState } from "react";
import { CREATE_STEPS, type StepState } from "./createSteps";

/**
 * Step navigation for manual invoice creation. A step counts as done once it was left through
 * "Weiter" (each step decides when it may continue). Done steps — and the first open step after
 * them — can be opened directly from the stepper or the overview row; going back never un-does
 * a later step.
 */
export function useCreateWizard() {
    const last = CREATE_STEPS.length - 1;
    const [current, setCurrent] = useState(0);
    const [completed, setCompleted] = useState(-1);

    const next = useCallback(() => {
        setCompleted((done) => Math.max(done, current));
        setCurrent(Math.min(current + 1, last));
    }, [current, last]);

    const back = useCallback(() => setCurrent((c) => Math.max(c - 1, 0)), []);

    const canOpen = useCallback((index: number) => index >= 0 && index <= Math.min(completed + 1, last), [completed, last]);

    const goTo = useCallback((index: number) => {
        if (canOpen(index)) setCurrent(index);
    }, [canOpen]);

    const stateOf = (index: number): StepState =>
        index === current ? "current" : index <= completed ? "done" : "upcoming";

    /** Reopen at `step` with every step before it done — resuming a draft, or starting over (0). */
    const restore = useCallback((step: number) => {
        const s = Math.max(0, Math.min(step, last));
        setCurrent(s);
        setCompleted(s - 1);
    }, [last]);

    return { current, isFirst: current === 0, isLast: current === last, next, back, goTo, canOpen, stateOf, restore };
}

export type CreateWizard = ReturnType<typeof useCreateWizard>;
